import { useState, useEffect, useRef } from 'react'
import {
  Upload, FileText, X, Loader2, ChevronDown, ChevronUp, Check,
  BookOpen, FileSearch, Mic, Clipboard, MessageSquare, File,
} from 'lucide-react'
import api from '../lib/api'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

const SOURCE_TYPES = [
  { value: 'reference',       label: 'Reference Document',     icon: BookOpen,  color: '#0f766e' },
  { value: 'sop',             label: 'Statement of Purpose',    icon: FileText,  color: '#2563eb' },
  { value: 'synopsis',        label: 'Synopsis',                icon: BookOpen,  color: '#7c3aed' },
  { value: 'proposal',        label: 'Research Proposal',       icon: FileSearch,color: '#0891b2' },
  { value: 'draft',           label: 'Existing Draft',          icon: File,      color: '#16a34a' },
  { value: 'literature_notes',label: 'Literature Notes',        icon: Clipboard, color: '#d97706' },
  { value: 'supervisor',      label: 'Supervisor Comments',     icon: MessageSquare, color: '#dc2626' },
  { value: 'transcript',      label: 'Interview Transcript',    icon: Mic,       color: '#0d9488' },
  { value: 'other',           label: 'Other Document',          icon: File,      color: '#64748b' },
]

function sourceTypeCfg(type) {
  return SOURCE_TYPES.find(t => t.value === type) || SOURCE_TYPES[SOURCE_TYPES.length - 1]
}

function fmtSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// ── Source card ───────────────────────────────────────────────────────────────

const apaFallback = (source) => {
  const m = source.parsed_metadata || {}, c = m.citation || {}
  if (c.apa) return c.apa
  const authors = (c.authors || 'Unknown author').replace(/\.+$/, '')
  const title = c.title || m.title || (source.original_filename || '').replace(/\.[^.]+$/, '')
  return `${authors}. (${c.year || 'n.d.'}). ${title}.${c.publication ? ` ${c.publication}.` : ''}`
}

const TONES = { green: ['#15803d', '#16a34a14'], amber: ['#b45309', '#d9770614'], grey: ['#64748b', '#64748b12'], blue: ['#2563eb', '#2563eb12'] }
function Note({ tone, children }) {
  const [color, bg] = TONES[tone]
  return (
    <span className="inline-flex items-center gap-1 font-sans text-xs px-2 py-0.5 rounded" style={{ color, background: bg }}>
      {children}
    </span>
  )
}

// What was read: honest about truncation and missing analysis, never a bare "uploaded".
function ReadStatus({ ex }) {
  if (!ex) return <Note tone="grey">Uploaded before reading stats existed. Re-upload to see how much was read</Note>
  const pages = ex.pages ? `${ex.pages} pages · ` : ''
  const words = (ex.words || 0).toLocaleString()
  if (ex.truncated) {
    const kept = Math.round(ex.words * ex.stored_chars / ex.chars).toLocaleString()
    return <Note tone="amber">Read the first {kept} of {words} words ({pages}the rest is too long to keep)</Note>
  }
  return <Note tone="green"><Check style={{ width: 11, height: 11 }} /> Read in full · {pages}{words} words</Note>
}

const FieldLabel = ({ children }) => (
  <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-1">{children}</p>
)

export function SourceCard({ source, onDelete, onChanged, thesisId }) {
  const [expanded, setExpanded] = useState(false)
  const [editing,  setEditing]  = useState(false)
  const [apa,      setApa]      = useState('')
  const [busy,     setBusy]     = useState(false)
  const cfg    = sourceTypeCfg(source.source_type)
  const Icon   = cfg.icon
  const meta   = source.parsed_metadata || {}
  const points = meta.key_points || []
  const usage  = Object.entries(meta.usage || {})
  const hasMore = !!(meta.objectives?.length || meta.frameworks?.length || meta.hypotheses?.length || meta.key_themes?.length || meta.methodology)

  const startEdit = () => { setApa(apaFallback(source)); setEditing(true) }
  const saveReference = async () => {
    setBusy(true)
    try {
      const { data } = await api.post(`/thesis/${thesisId}/sources/${source.id}/use-as-reference`, { apa })
      onChanged?.({ ...source, ...data.source })
      setEditing(false)
      toast.success('Added to your reference list')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add to the reference list')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${cfg.color}30`, background: '#fff' }}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${cfg.color}15` }}>
          <Icon style={{ width: 14, height: 14, color: cfg.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-sans text-sm font-medium text-acdyon-text truncate">{source.original_filename}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-sans text-xs px-1.5 py-0.5 rounded" style={{ background: `${cfg.color}12`, color: cfg.color }}>{cfg.label}</span>
            {source.file_size && <span className="font-mono text-xs text-acdyon-textFaint">{fmtSize(source.file_size)}</span>}
          </div>
        </div>
        {onDelete && (
          <button onClick={() => onDelete(source.id)} className="p-1.5 rounded-lg transition-colors hover:text-red-400" style={{ color: '#94a3b8' }} title="Remove document">
            <X style={{ width: 13, height: 13 }} />
          </button>
        )}
      </div>

      {/* Proof it was read, and where it is used */}
      <div className="px-4 pb-3 flex flex-wrap gap-1.5">
        <ReadStatus ex={meta.extraction} />
        {meta.extraction?.analysis_failed && <Note tone="amber">Key points unavailable, the text is still used</Note>}
        {usage.length > 0
          ? usage.map(([ch, u]) => <Note key={ch} tone="blue">Used in Ch {ch} · {u.passages} passages</Note>)
          : <Note tone="grey">Not used yet. It is used when you generate a chapter</Note>}
      </div>

      {/* Points taken from the document */}
      {points.length > 0 && (
        <div className="px-4 pb-3" style={{ borderTop: `1px solid ${cfg.color}18` }}>
          <div className="pt-3"><FieldLabel>Key points taken from this document</FieldLabel></div>
          <ul className="space-y-1">
            {(expanded ? points : points.slice(0, 3)).map((pt, i) => (
              <li key={i} className="font-sans text-xs text-acdyon-textMuted flex gap-1.5">
                <span style={{ color: cfg.color, flexShrink: 0 }}>•</span>{pt}
              </li>
            ))}
          </ul>
          {(points.length > 3 || hasMore) && (
            <button onClick={() => setExpanded(v => !v)} className="mt-2 inline-flex items-center gap-1 font-sans text-xs" style={{ color: cfg.color }}>
              {expanded ? <ChevronUp style={{ width: 12, height: 12 }} /> : <ChevronDown style={{ width: 12, height: 12 }} />}
              {expanded ? 'Show less' : 'Show all'}
            </button>
          )}
        </div>
      )}

      {expanded && hasMore && (
        <div className="px-4 pb-3 space-y-2">
          {meta.methodology && <div><FieldLabel>Methodology</FieldLabel><p className="font-sans text-xs text-acdyon-textMuted">{meta.methodology}</p></div>}
          {meta.objectives?.length > 0 && <div><FieldLabel>Objectives</FieldLabel><ul className="space-y-0.5">{meta.objectives.slice(0, 5).map((o, i) => <li key={i} className="font-sans text-xs text-acdyon-textMuted">• {o}</li>)}</ul></div>}
          {meta.hypotheses?.length > 0 && <div><FieldLabel>Hypotheses</FieldLabel><ul className="space-y-0.5">{meta.hypotheses.slice(0, 3).map((h, i) => <li key={i} className="font-sans text-xs text-acdyon-textMuted">H{i + 1} {h}</li>)}</ul></div>}
          {meta.frameworks?.length > 0 && <div><FieldLabel>Frameworks</FieldLabel><div className="flex flex-wrap gap-1">{meta.frameworks.map((f, i) => <span key={i} className="font-sans text-xs px-2 py-0.5 rounded-full" style={{ background: `${cfg.color}12`, color: cfg.color }}>{f}</span>)}</div></div>}
          {meta.key_themes?.length > 0 && <div><FieldLabel>Key themes</FieldLabel><div className="flex flex-wrap gap-1">{meta.key_themes.map((t, i) => <span key={i} className="font-sans text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>{t}</span>)}</div></div>}
        </div>
      )}

      {/* Use the document itself as a reference */}
      {onChanged && (
        <div className="px-4 py-3" style={{ borderTop: `1px solid ${cfg.color}18`, background: '#fafbfc' }}>
          {editing ? (
            <div className="space-y-2">
              <FieldLabel>Citation (APA 7). Check it, then save</FieldLabel>
              <textarea
                value={apa} onChange={e => setApa(e.target.value)} rows={3}
                className="w-full font-sans text-xs rounded-md p-2"
                style={{ border: '1px solid #DDE4EF', outline: 'none', resize: 'vertical' }}
              />
              <div className="flex gap-2">
                <button onClick={saveReference} disabled={busy || !apa.trim()}
                  className="font-sans text-xs font-semibold px-3 py-1.5 rounded-md"
                  style={{ background: '#1e3a5f', color: '#fff', opacity: busy || !apa.trim() ? 0.6 : 1 }}>
                  {busy ? 'Saving…' : 'Save to reference list'}
                </button>
                <button onClick={() => setEditing(false)} className="font-sans text-xs px-3 py-1.5 rounded-md" style={{ border: '1px solid #DDE4EF', color: '#64748b' }}>Cancel</button>
              </div>
            </div>
          ) : source.in_reference_list ? (
            <div className="flex items-center justify-between gap-2">
              <Note tone="green"><Check style={{ width: 11, height: 11 }} /> In your reference list</Note>
              <button onClick={startEdit} className="font-sans text-xs" style={{ color: '#2563eb' }}>Edit citation</button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <p className="font-sans text-xs text-acdyon-textFaint">Cite this document in your thesis.</p>
              <button onClick={startEdit} className="font-sans text-xs font-semibold px-3 py-1.5 rounded-md" style={{ background: '#1e3a5f', color: '#fff' }}>Use as reference</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function ResearchSourcesPanel({ thesisId }) {
  const [sources,     setSources]     = useState([])
  const [loading,     setLoading]     = useState(true)
  const [uploading,   setUploading]   = useState(false)
  const [dragOver,    setDragOver]    = useState(false)
  const [sourceType,  setSourceType]  = useState('reference')
  const fileRef = useRef(null)

  const load = async () => {
    setLoading(true)
    try {
      try {
        const { data } = await api.get(`/thesis/${thesisId}/sources`)
        setSources(data || [])
      } catch {
        const { data } = await supabase
          .from('thesis_sources')
          .select('id, source_type, original_filename, parsed_metadata, file_size, created_at')
          .eq('thesis_id', thesisId)
          .order('created_at', { ascending: false })
        setSources(data || [])
      }
    } catch { /* silent */ } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (thesisId) load() }, [thesisId]) // eslint-disable-line react-hooks/exhaustive-deps

  // One file at a time. The server reads the text (docx/doc/pdf/txt) and refuses what it
  // cannot read; the old fallback saved an empty row, so the file looked used when it was not.
  const uploadFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['txt', 'docx', 'doc', 'pdf'].includes(ext)) {
      toast.error(`${file.name}: only DOCX, DOC, PDF and TXT files are supported`)
      return
    }
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('source_type', sourceType)
      const { data } = await api.post(`/thesis/${thesisId}/upload-source`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setSources(prev => [data.source, ...prev])
      toast.success(`${file.name}: read ${data.source.word_count?.toLocaleString() || ''} words`)
    } catch (err) {
      toast.error(`${file.name}: ${err.response?.data?.error || 'upload failed'}`)
    }
  }

  const uploadFiles = async (files) => {
    setUploading(true)
    for (const f of files) await uploadFile(f)
    setUploading(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length) uploadFiles([...e.dataTransfer.files])
  }

  const handleDelete = async (sourceId) => {
    try {
      await api.delete(`/thesis/${thesisId}/sources/${sourceId}`)
    } catch {
      await supabase.from('thesis_sources').delete().eq('id', sourceId)
    }
    setSources(prev => prev.filter(s => s.id !== sourceId))
    toast.success('Source removed')
  }

  return (
    <div className="space-y-4">

      {/* Source type picker */}
      <div>
        <label className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide block mb-2">
          Document Type
        </label>
        <div className="flex flex-wrap gap-1.5">
          {SOURCE_TYPES.map(({ value, label, icon: Icon, color }) => {
            const active = sourceType === value
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSourceType(value)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-sans text-xs transition-all"
                style={{
                  background: active ? `${color}15` : 'transparent',
                  color: active ? color : '#64748b',
                  border: `1px solid ${active ? color + '40' : '#DDE4EF'}`,
                }}
              >
                <Icon style={{ width: 11, height: 11 }} />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        className="relative flex flex-col items-center justify-center gap-3 rounded-xl cursor-pointer transition-all"
        style={{
          padding: '2rem 1.5rem',
          border: `2px dashed ${dragOver ? '#2563eb' : '#DDE4EF'}`,
          background: dragOver ? 'rgba(37,99,235,0.04)' : '#fafbfc',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".txt,.docx,.doc,.pdf"
          multiple
          className="sr-only"
          onChange={e => { if (e.target.files.length) uploadFiles([...e.target.files]); e.target.value = '' }}
        />
        {uploading ? (
          <>
            <Loader2 style={{ width: 20, height: 20, color: '#2563eb' }} className="animate-spin" />
            <p className="font-sans text-sm text-acdyon-textMuted">Reading your documents…</p>
          </>
        ) : (
          <>
            <Upload style={{ width: 20, height: 20, color: '#94a3b8' }} />
            <div className="text-center">
              <p className="font-sans text-sm font-medium text-acdyon-textMuted">
                Drop files or click to upload
              </p>
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">
                DOCX, DOC, PDF, TXT · up to 50 MB each
              </p>
            </div>
          </>
        )}
      </div>

      {/* Source list */}
      {loading ? (
        <div className="py-4 text-center">
          <Loader2 style={{ width: 16, height: 16, color: '#94a3b8' }} className="animate-spin mx-auto" />
        </div>
      ) : sources.length === 0 ? (
        <p className="font-sans text-xs text-acdyon-textFaint text-center py-3">
          No documents yet. Upload the papers and notes your thesis should be built on.
        </p>
      ) : (
        <div className="space-y-2">
          {sources.map(s => (
            <SourceCard key={s.id} source={s} onDelete={handleDelete} thesisId={thesisId} onChanged={u => setSources(prev => prev.map(x => x.id === u.id ? u : x))} />
          ))}
        </div>
      )}
    </div>
  )
}
